// HR module (minimal, restricted): employee profiles and leave requests.
import { z } from 'zod';

const Uuid = z.string().uuid();
const DateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data non valida');

export const EMPLOYMENT_TYPES = ['full_time', 'part_time', 'contractor'] as const;
export type EmploymentType = (typeof EMPLOYMENT_TYPES)[number];
export const EMPLOYMENT_TYPE_LABELS: Record<EmploymentType, string> = { full_time: 'Tempo pieno', part_time: 'Part-time', contractor: 'Collaboratore' };

export const LEAVE_KINDS = ['vacation', 'sick', 'other'] as const;
export type LeaveKind = (typeof LEAVE_KINDS)[number];
export const LEAVE_KIND_LABELS: Record<LeaveKind, string> = { vacation: 'Ferie', sick: 'Malattia', other: 'Altro' };

export const LEAVE_STATUSES = ['pending', 'approved', 'rejected'] as const;
export type LeaveStatus = (typeof LEAVE_STATUSES)[number];
export const LEAVE_STATUS_LABELS: Record<LeaveStatus, string> = { pending: 'In attesa', approved: 'Approvata', rejected: 'Rifiutata' };

export const EmployeeProfileInput = z.object({
  jobTitle: z.string().trim().max(100).nullable().default(null),
  employmentType: z.enum(EMPLOYMENT_TYPES).default('full_time'),
  startDate: DateOnly.nullable().default(null),
  leaveBalanceDays: z.number().min(0).max(365).default(20),
});
export type EmployeeProfileInput = z.input<typeof EmployeeProfileInput>;

export const LeaveRequestInput = z.object({
  kind: z.enum(LEAVE_KINDS),
  startDate: DateOnly,
  endDate: DateOnly,
  days: z.number().positive().max(365),
  note: z.string().trim().max(500).nullable().default(null),
});
export type LeaveRequestInput = z.input<typeof LeaveRequestInput>;

export const ReviewLeaveInput = z.object({ status: z.enum(['approved', 'rejected']) });

// ------------------------------------------------------------------ responses
export interface Employee {
  userId: string;
  displayName: string;
  email: string;
  jobTitle: string | null;
  employmentType: EmploymentType;
  startDate: string | null;
  leaveBalanceDays: number;
  pendingLeaveDays: number;
  canEdit: boolean;
}

export interface LeaveRequest {
  id: string;
  workspaceId: string;
  user: { id: string; displayName: string };
  kind: LeaveKind;
  startDate: string;
  endDate: string;
  days: number;
  note: string | null;
  status: LeaveStatus;
  reviewedBy: { id: string; displayName: string } | null;
  reviewedAt: string | null;
  createdAt: string;
  canReview: boolean;
  canCancel: boolean;
}

export interface HrConfig {
  workspaceId: string;
  enabled: boolean;
  permissions: { view: boolean; admin: boolean };
}
